import React from 'react';

import { isPlatform, setupIonicReact } from '@ionic/react';
import { StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import * as Sentry from '@sentry/capacitor';
import { SnackbarProvider } from 'notistack';
import 'react-datepicker/dist/react-datepicker.min.css';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { SentryDeploymentContext, SentryTag } from './config/constants';
import { DarkModeProvider, useDarkMode } from './hooks/useDarkMode';
import { createTheme } from './muiTheme';
import { EnvironmentProvider } from './providers/EnvironmentProvider';
import {
	ENV_DEFAULTS,
	getStringEnvVar,
} from './providers/EnvironmentProvider/config';

setupIonicReact({
	mode: 'md',
});

const isProduction = getStringEnvVar('VITE_ENVIRONMENT') === 'PRODUCTION';

if (isProduction) {
	Sentry.init({
		dsn: 'https://dcf3784cd0165960954e18e2ab0a3cb5@o4508646567837696.ingest.us.sentry.io/4508646613319680',
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
			}),
		],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Session Replay
		replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		release: `asaph@${ENV_DEFAULTS.version}`,
		// environment: isProduction ? 'production' : 'development',
	});

	Sentry.setTag(
		SentryTag.DEPLOYMENT,
		isPlatform('capacitor')
			? SentryDeploymentContext.NATIVE_APP
			: SentryDeploymentContext.WEB,
	);
}

const AppWrapper: React.FC = () => {
	const { palette } = useDarkMode();

	const theme = React.useMemo(() => createTheme(palette), [palette]);

	return (
		<StylesProvider injectFirst>
			<ThemeProvider theme={theme}>
				<EnvironmentProvider>
					<SnackbarProvider
						maxSnack={3}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						classes={{
							variantSuccess: 'snackbar-success',
							containerAnchorOriginBottomLeft:
								'snackbar-container',
						}}
					>
						<BrowserRouter>
							<App />
						</BrowserRouter>
					</SnackbarProvider>
				</EnvironmentProvider>
			</ThemeProvider>
		</StylesProvider>
	);
};

ReactDOM.render(
	<DarkModeProvider>
		<AppWrapper />
	</DarkModeProvider>,
	document.getElementById('root'),
);
